<template>
  <div class="mpid-details-page">
    <!-- App Header -->
    <AppHeader :title="headerTitle" class="app-header" />

    <!-- Loading overlay when all data is loading -->
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading device data...</div>
    </div>

    <!-- Error message -->
    <div v-else-if="error" class="error-container">
      <div class="error-message">{{ error }}</div>
      <button class="action-button" @click="fetchDeviceData">Retry</button>
    </div>

    <!-- Full page background with 2 rows and 3 columns containing content sections -->
    <div v-else class="container-fluid">
      <!-- Top row -->
      <div class="row h-50">
        <!-- Metering Point Info column -->
        <div class="col-md-4 section-col p-0">
          <MpidDetailsInfo
              :meteringPoint="mp"
              :mpidValue="routeMpid"
              :isLoading="mpLoading"
              :error="mpError"
              :deviceId="device?.logical_device_number || ''"
              :deviceType="device?.type || ''"
              :linkedMeteringPoint="linkedMeterMpData"
              :linkedMeterLDN="hasActiveMeter ? activeMeterLDN : ''"
              :hasActiveMeter="hasActiveMeter"
              @mpid-updated="handleMpidUpdated"
          />
        </div>

        <!-- Device Details Info column -->
        <div class="col-md-4 section-col  p-0">
          <MpidDeviceDetailsInfo
              :device="device"
              :isLoading="deviceInfoLoading"
              :error="deviceError"
              @open-attach-device="openAttachDeviceOverlay"
              @show-add-radio-overlay="showAddRadioOverlay"
              @show-add-meter-overlay="showAddMeterOverlay"
              @show-remove-radio-overlay="showRemoveRadioOverlay"
              @show-remove-meter-overlay="showRemoveMeterOverlay"
              @show-edit-overlay="showEditOverlay"
              @retry="fetchDeviceData"
          />
        </div>

        <!-- Map column (spans both rows) -->
        <div class="col-md-4 section-col  map-column p-0">
          <div ref="mpidMap" class="mpid-map"></div>
          <div class="map-overlay"></div>
        </div>
      </div>

      <!-- Bottom row -->
      <div class="row h-50">
        <!-- Readings column -->
        <div class="col-md-4 section-col l p-0">
          <MpidDetailsReadings
              :deviceId="device?.logical_device_number || ''"
              :deviceProtocol="device?.protocol || ''"
              :deviceSystemTitle="device?.logical_device_number || ''"
          />
        </div>

        <!-- Consumption chart column -->
        <div class="col-md-4 section-col p-0">
          <MpidDetailsReadingsChart
              :deviceId="device?.logical_device_number || ''"
          />
        </div>

        <!-- Map column continues (no content needed here as map spans both rows) -->
        <div class="col-md-4 section-col section-col-alt map-column p-0" style="visibility: hidden;">
          <!-- Hidden placeholder to maintain grid structure -->
        </div>
      </div>
    </div>

    <!-- Attach Device Overlay -->
    <MpidDetailAddDeviceOverlay
        v-if="showAttachDeviceOverlay"
        :isVisible="showAttachDeviceOverlay"
        :mpid="routeMpid"
        @close="closeAttachDeviceOverlay"
        @attached="handleDeviceAttached"
    />
  </div>
</template>

<script>
import AppHeader from './AppHeader.vue';
import MpidDetailsInfo from '@/components/MpidDetailsInfo.vue';
import MpidDeviceDetailsInfo from '@/components/MpidDeviceDetailsInfo.vue';
import MpidDetailsReadings from '@/components/MpidDetailsReadings.vue';
import MpidDetailsReadingsChart from '@/components/MpidDetailsReadingsChart.vue';
import MpidDetailAddDeviceOverlay from '@/components/MpidDetailAddDeviceOverlay.vue';
import MpidApiService from '@/services/MpidApiService';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export default {
  name: 'MpidDetailsPage',
  components: {
    AppHeader,
    MpidDetailsInfo,
    MpidDeviceDetailsInfo,
    MpidDetailsReadings,
    MpidDetailsReadingsChart,
    MpidDetailAddDeviceOverlay
  },

  data() {
    return {
      // MP data
      mp: null,
      mpLoading: false,
      mpError: '',

      // Device (last active device on this MP)
      device: null,
      deviceInfoLoading: false,
      deviceError: '',

      // Other placeholders already used by template
      isLoading: false,
      error: '',

      meteringPoint: null,
      mpidLoading: false,
      mpidError: '',
      linkedMeterMpData: null,
      hasActiveMeter: false,
      activeMeterLDN: '',
      readings: [],

      // Overlay states
      showAttachDeviceOverlay: false,

      // Map state
      map: null,
      mapSourceLoaded: false
    };
  },

  computed: {
    headerTitle() {
      return this.routeMpid ? `${this.routeMpid}` : 'MPID';
    },
    routeMpid() {
      return this.$route?.params?.mpid || '';
    }
  },

  watch: {
    routeMpid: {
      immediate: true,
      handler() {
        this.fetchMp();
      }
    },
    mp: {
      handler() {
        this.$nextTick(() => this.updateMapData());
      },
      deep: true
    }
  },

  methods: {
    async fetchMp() {
      if (!this.routeMpid) {
        this.mp = null;
        this.device = null;
        return;
      }
      this.mpLoading = true;
      this.deviceInfoLoading = true;
      this.mpError = '';
      this.deviceError = '';

      try {
        const mp = await MpidApiService.getMeteringPoint(this.routeMpid);
        this.mp = mp;

        // Map last_active_device (if present) into the device object for the MP page
        const lad = mp?.last_active_device || null;
        this.device = lad
            ? {
              logical_device_number: lad.logical_device_number || null,
              customer_number: lad.customer_number || null,
              type: lad.type || null,
              is_integrated: !!lad.is_integrated,
              protocol: lad.protocol || '',
              distribution_company: lad.distribution_company || '',
              installed: lad.installed || null,
              caliber: lad.caliber || null,
              id_radio: lad.id_radio || null,
              lora_eui: lad.lora_eui || null,
              removed: lad.removed || null,
              // Optional arrays if backend provides them
              last_active_radio: lad.last_active_radio || [],
              last_active_meter: lad.last_active_meter || []
            }
            : null;

      } catch (e) {
        console.error(e);
        this.mpError = 'Failed to load metering point';
        this.deviceError = 'No device attached to this MPID';
        this.mp = null;
        this.device = null;
      } finally {
        this.mpLoading = false;
        this.deviceInfoLoading = false;
      }
    },

    initializeMap() {
      if (this.map || !this.$refs.mpidMap) return;

      this.map = new maplibregl.Map({
        container: this.$refs.mpidMap,
        style: 'https://api.maptiler.com/maps/basic-v2-dark/style.json?key=4DXCyU0jQcYTU9cFAGQu',
        center: [9.1859, 45.4654],
        zoom: 10,
        attributionControl: false
      });

      this.map.on('load', () => {
        // Hide city/town/village labels
        const layers = (this.map.getStyle() && this.map.getStyle().layers) || [];
        layers.forEach(l => {
          const srcLayer = l['source-layer'] || '';
          if (
              l.type === 'symbol' &&
              (
                  /place|settlement/i.test(srcLayer) ||
                  /place|city|town|village|hamlet|settlement/i.test(l.id)
              )
          ) {
            this.map.setLayoutProperty(l.id, 'visibility', 'none');
          }
        });

        // Add source for the metering point
        this.map.addSource('mpid-point', {
          type: 'geojson',
          data: { type: 'FeatureCollection', features: [] }
        });

        // Add layer for the metering point
        this.map.addLayer({
          id: 'mpid-marker',
          type: 'circle',
          source: 'mpid-point',
          paint: {
            'circle-color': '#4fc3f7',
            'circle-radius': 12,
            'circle-stroke-width': 3,
            'circle-stroke-color': '#ffffff',
            'circle-opacity': 0.95
          }
        });

        this.mapSourceLoaded = true;
        this.updateMapData();
      });
    },
    handleMpidUpdated(updatedMeteringPoint) {
      // Update the local mp data with the updated information
      this.mp = { ...this.mp, ...updatedMeteringPoint };
      // Optionally refresh all data
      this.fetchMp();
    },

    updateMapData() {
      if (!this.map || !this.mapSourceLoaded || !this.mp) return;

      const source = this.map.getSource('mpid-point');
      if (!source) return;

      // Check if the metering point has valid coordinates
      if (this.mp.lat && this.mp.lon &&
          !isNaN(parseFloat(this.mp.lat)) && !isNaN(parseFloat(this.mp.lon))) {

        const lat = parseFloat(this.mp.lat);
        const lon = parseFloat(this.mp.lon);

        // Create feature for the metering point
        const feature = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [lon, lat]
          },
          properties: {
            mpid: this.mp.mpid || '',
            street: this.mp.street || '',
            city: this.mp.city || '',
            customer: this.mp.customer_fname || ''
          }
        };

        source.setData({ type: 'FeatureCollection', features: [feature] });

        // Center the map on the point
        this.map.flyTo({
          center: [lon, lat],
          zoom: 15,
          duration: 1000
        });
      } else {
        // Clear the source if no valid coordinates
        source.setData({ type: 'FeatureCollection', features: [] });
      }
    },

    // Event handlers for device overlay actions
    openAttachDeviceOverlay() {
      this.showAttachDeviceOverlay = true;
    },

    closeAttachDeviceOverlay() {
      this.showAttachDeviceOverlay = false;
    },

    handleDeviceAttached() {
      this.closeAttachDeviceOverlay();
      this.fetchMp(); // reload MP + last_active_device
    },

    // Event handlers for device actions (placeholder implementations)
    showAddRadioOverlay() {
      console.log('Show add radio overlay');
      // TODO: Implement add radio overlay
    },

    showAddMeterOverlay() {
      console.log('Show add meter overlay');
      // TODO: Implement add meter overlay
    },

    showRemoveRadioOverlay() {
      console.log('Show remove radio overlay');
      // TODO: Implement remove radio overlay
    },

    showRemoveMeterOverlay() {
      console.log('Show remove meter overlay');
      // TODO: Implement remove meter overlay
    },

    showEditOverlay() {
      console.log('Show edit overlay');
      // TODO: Implement edit overlay
    },

    // Other event handlers used by child components
    handleDeviceUpdated() {
      this.fetchMp(); // reload data when device is updated
    },

    fetchDeviceData() {
      this.fetchMp(); // reload all data
    },

    fetchMeteringPoint() {
      this.fetchMp(); // reload all data
    },

    handleMpidAttached() {
      this.fetchMp(); // reload data when MPID is attached
    },

    handleMpidRemoved() {
      this.fetchMp(); // reload data when MPID is removed
    },

    fetchAllData() {
      this.fetchMp(); // reload all data
    },

    exportReadingsCsv() {
      console.log('Export readings to CSV');
      // TODO: Implement CSV export
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.initializeMap();
    });
  },

  beforeUnmount() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }
};
</script>

<style scoped>
/* ===========================================
   BASE LAYOUT & STRUCTURE
   =========================================== */
.mpid-details-page {
  height: 100vh;
  color: white;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;
}

/* Fixed header styling */
.app-header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
}

/* Full height/width grid layout */
.container-fluid {
  height: 100vh;
  width: 100%;
  max-width: 100%;
  padding: 0;
  position: absolute;
  top: 0;
  left: 0;
}

.row {
  margin: 0;
  padding: 0;
}

.h-50 {
  height: 50%;
}

/* Section columns styling */
.section-col {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 24px !important;
}

.section-col-alt {
  background-color: rgba(86, 86, 86, 0.1);
}

/* Map column specific styling */
.map-column {
  position: absolute !important;
  right: 0;
  top: 0;
  height: 100vh !important;
  width: 33.333333% !important;
  padding: 0 !important;
}

.mpid-map {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.map-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
      to bottom,
      rgba(27, 26, 26, 0.15) 0%,
      rgba(23, 23, 23, 0.05) 100%
  );
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  pointer-events: none;
}

/* Keep map controls above */
:deep(.maplibregl-ctrl) {
  z-index: 2;
}

/* Section styling */
.section-content {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: white;
  display: inline-block;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.section-header-alt {
  margin-top: 80px;
}

/* Device details grid */
.device-details-grid {
  margin-top: 35px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.device-info-row {
  margin-bottom: 25px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.device-info-label {
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.05em;
  opacity: 0.6;
  text-align: left;
  text-transform: uppercase;
}

.device-info-value {
  font-size: 11px;
  font-weight: 500;
}

/* Status Colors */
.status-yes {
  color: #4caf50 !important;
}

.status-no {
  color: #f44336 !important;
}

/* ===========================================
   LOADING STATES
   =========================================== */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  gap: 20px;
}

.loading-text {
  color: white;
  font-size: 16px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ===========================================
   ERROR STATES
   =========================================== */
.error-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1001;
  gap: 20px;
  padding: 20px;
  text-align: center;
}

.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  gap: 15px;
}

.error-message {
  color: #ff8a80;
  text-align: center;
}

.retry-button {
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 6px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.retry-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* ===========================================
   EMPTY STATES
   =========================================== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  opacity: 0.8;
  text-align: center;
}

.info-icon {
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 16px;
}

.empty-state p {
  font-size: 13px;
  max-width: 80%;
  margin-bottom: 8px;
}

.empty-state small {
  font-size: 11px;
  opacity: 0.7;
}

/* ===========================================
   BUTTONS & ACTIONS
   =========================================== */
.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 12px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

/* ===========================================
   RESPONSIVE DESIGN
   =========================================== */
@media (max-width: 767.98px) {
  .row {
    height: auto !important;
    flex-direction: column;
  }

  .section-col {
    height: auto !important;
    min-height: 300px;
  }

  .map-column {
    position: relative !important;
    height: 300px !important;
    width: 100% !important;
  }
}
</style>